import { create } from "zustand";
import { io } from "socket.io-client";
import axios from "axios";
import swal from "sweetalert";
import { isAccountVerified } from "../../Components/Authentication/SharedLogItem/Verification";

const SOCKET_URL = "https://yeapbe.com:3680/";
const LOGIN_URL = "https://aps.songjukti.com/profile/fun_uinsert.php";
const IP_URL = "https://geolocation-db.com/json/";

// The one key every component reads via decodeLocalData("gh_sl@g").
export const USER_KEY = "gh_sl@g";
const REMEMBER_KEY = "rememberedEmail";

// Cross-subdomain SSO cookie shared with songjukti.com / user.songjukti.com.
const SSO_COOKIE = "songjukti_user";

// Keys written by older versions of the app. They are wiped on logout so
// stale data can never resurrect a session. ("ip" is intentionally NOT here.)
const LEGACY_KEYS = ["user", "songjukti_user", "songjukti_user@reg", "RData"];

/* ------------------------------------------------------------------ */
/* Storage helpers                                                     */
/* ------------------------------------------------------------------ */

// btoa() throws on non-Latin1 characters (e.g. Bangla names). Escaping them
// as \uXXXX keeps the JSON pure ASCII, so btoa works AND the legacy
// atob() + JSON.parse() readers still get the original characters back.
const toAscii = (json) =>
  json.replace(
    /[\u0080-\uFFFF]/g,
    (c) => "\\u" + c.charCodeAt(0).toString(16).padStart(4, "0")
  );

const persistUser = (account) => {
  try {
    window.localStorage.setItem(USER_KEY, btoa(toAscii(JSON.stringify(account))));
    return true;
  } catch (error) {
    console.error("Failed to persist session:", error);
    return false;
  }
};

const readStoredUser = () => {
  try {
    const raw = window.localStorage.getItem(USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(atob(raw));
    return parsed?.sopnoid ? parsed : null;
  } catch {
    return null; // corrupt / stale -> treat as logged out
  }
};

/* ------------------------------------------------------------------ */
/* SSO cookie helpers                                                  */
/* ------------------------------------------------------------------ */

const cookieAttrs = () => {
  const onBrandDomain = window.location.hostname.endsWith("songjukti.com");
  return onBrandDomain
    ? "path=/;domain=.songjukti.com;samesite=lax;secure"
    : "path=/;samesite=lax"; // localhost / preview builds
};

const writeSsoCookie = (account) => {
  try {
    const value = encodeURIComponent(JSON.stringify(account));
    if (value.length > 3800) {
      console.warn("Session too large for a cookie — skipping SSO cookie.");
      return;
    }
    document.cookie = `${SSO_COOKIE}=${value};${cookieAttrs()}`;
  } catch (error) {
    console.error("Failed to write SSO cookie:", error);
  }
};

const clearSsoCookie = () => {
  document.cookie = `${SSO_COOKIE}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;${cookieAttrs()}`;
};

const readSsoCookie = () => {
  try {
    const row = document.cookie
      .split("; ")
      .find((r) => r.startsWith(SSO_COOKIE + "="));
    if (!row) return null;
    let value = JSON.parse(decodeURIComponent(row.slice(SSO_COOKIE.length + 1)));
    // Older builds double-stringified the cookie — unwrap that too.
    if (typeof value === "string") value = JSON.parse(value);
    return value?.sopnoid ? value : null;
  } catch {
    return null;
  }
};

/* ------------------------------------------------------------------ */
/* Misc helpers                                                        */
/* ------------------------------------------------------------------ */

// The PHP endpoint sometimes prepends whitespace/notices; be tolerant.
const parseAccount = (data) => {
  let d = data;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d.trim());
    } catch {
      return null;
    }
  }
  const account = Array.isArray(d) ? d[0] : d;
  return account?.sopnoid ? account : null;
};

const pad = (n) => String(n).padStart(2, "0");

const buildTimestamps = () => {
  const d = new Date();
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return {
    time: `${date} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`,
    currentDate: date,
  };
};

const initialData = () => ({
  onlineUsers: [],
  allNotification: [],
  invitationNotification: [],
  messageNotification: [],
  msgUserList: [],
  noteData: [],
});

/* ------------------------------------------------------------------ */
/* Store                                                               */
/* ------------------------------------------------------------------ */

export const useAuthStore = create((set, get) => ({
  // ---------- state ----------
  user: readStoredUser(),
  isLoggingIn: false,
  socket: null,
  ip: "",
  uniqId: "",
  temp: "",
  rememberedEmail:
    typeof window !== "undefined" ? localStorage.getItem(REMEMBER_KEY) || "" : "", // identifier only — NEVER the password
  ...initialData(),
  inComingMessageNoti: {},
  currentMSGRList: [],
  chatOpen: false,
  pageRefresh: false,
  notifSeen: true,
  userPage: 1,

  // ---------- simple setters ----------
  setUserPage: (state) => set({ userPage: state }),
  setAllNotification: (state) => set({ allNotification: state }),
  setCurrentMSGRList: (state) => set({ currentMSGRList: state }),
  setUser: (state) => set({ user: state }),
  setIp: (state) => set({ ip: state }),
  setTemp: (state) => set({ temp: state }),
  setResetPassOTP: (state) => set({ resetPassOTP: state }),
  setHashKey: (state) => set({ hashKey: state }),
  setSignLogSuccMsg: (state) => set({ signLogSuccMsg: state }),
  setRefetch: (state) => set({ refetch: state }),
  setInComingMessageNoti: (state) => set({ inComingMessageNoti: state }),
  setChatOpen: (state) => set({ chatOpen: state }),
  setPageRefresh: (state) => set({ pageRefresh: state }),
  setNotifSeen: (state) => set({ notifSeen: state }),
  setMsgUserList: (state) => set({ msgUserList: state }),
  handlePageRefresh: () => set({ pageRefresh: !get().pageRefresh }),

  fetchIp: async () => {
    if (get().ip) return get().ip;
    try {
      const res = await axios.get(IP_URL);
      const ip = res?.data?.IPv4 || "";
      if (ip) set({ ip });
      return ip;
    } catch (error) {
      console.error("Failed to fetch IP:", error);
      return "";
    }
  },

  // ---------- session ----------
  // The ONLY place a session is created. Writes localStorage + SSO cookie +
  // zustand together so they can never drift apart.
  //
  // An account whose mail_verify/phone_verify isn't 1 still gets a session
  // (so a refresh keeps them on the verify page), but it stays "quiet": no
  // SSO cookie, no socket, no data fetching, until it is verified.
  setSession: (account, { cookie = true } = {}) => {
    if (!account?.sopnoid) return false;
    const verified = isAccountVerified(account);
    persistUser(account);
    if (cookie && verified) writeSsoCookie(account);
    set({ user: account });
    if (verified) get().activateSession();
    return true;
  },

  // Starts the realtime + notification side of a verified session.
  activateSession: () => {
    get().connectSocket();
    get().fetchInvitationNotification();
    get().fetchMessageNotification();
  },

  // Merge changes into the current user (e.g. { mail_verify: "1" } after
  // OTP verification) and keep localStorage + cookie in sync.
  updateUser: (patch) => {
    const current = get().user;
    if (!current?.sopnoid) return;
    const next = { ...current, ...patch };
    const wasVerified = isAccountVerified(current);
    const nowVerified = isAccountVerified(next);
    persistUser(next);
    if (nowVerified) writeSsoCookie(next);
    set({ user: next });
    if (nowVerified && !wasVerified) get().activateSession();
  },

  // Local-only teardown (no network). Used by logout(), the 401 interceptor
  // and cross-tab logout.
  clearSession: () => {
    // Clear the in-memory user FIRST so the app tears down the signed-in UI
    // (Header etc.) before the storage keys it might still read disappear.
    set({ user: null, ...initialData() });
    get().disconnectSocket();
    LEGACY_KEYS.forEach((key) => window.localStorage.removeItem(key));
    window.localStorage.removeItem(USER_KEY);
    clearSsoCookie(); // otherwise the SSO cookie would log the user straight back in
  },

  // Adopt a session created on the main domain (cookie) if there is one.
  hydrateFromCookie: () => {
    if (get().user?.sopnoid) return true;
    const account = readSsoCookie();
    return account ? get().setSession(account, { cookie: false }) : false;
  },

  // Low-level credential check. Returns the account or null; throws on
  // network errors. Does NOT touch the session.
  authenticate: async (identifier, password) => {
    const formData = new FormData();
    formData.append("MobileNumber", identifier);
    formData.append("pass", password);
    formData.append("ip", get().ip || "");
    const res = await axios.post(LOGIN_URL, formData);
    return res.status === 200 ? parseAccount(res.data) : null;
  },

  login: async ({ email, password, rememberMe }) => {
    set({ isLoggingIn: true });
    try {
      const account = await get().authenticate(email, password);
      if (!account) {
        swal("Error", "Check Email or Password Again!", "error");
        return false;
      }

      if (rememberMe) localStorage.setItem(REMEMBER_KEY, email);
      else localStorage.removeItem(REMEMBER_KEY);
      set({ rememberedEmail: rememberMe ? email : "" });

      return get().setSession(account);
    } catch (error) {
      console.error("Login Error:", error);
      swal("Error", "Something went wrong. Please try again.", "error");
      return false;
    } finally {
      set({ isLoggingIn: false });
    }
  },

  // Used right after signup verification. Tries each identifier (phone,
  // email) with the password the person just chose. Silent — the caller
  // decides what to tell the user if it fails.
  autoLogin: async (identifiers, password) => {
    if (!password) return false;
    for (const id of identifiers.filter(Boolean)) {
      try {
        const account = await get().authenticate(id, password);
        if (account) return get().setSession(account);
      } catch (error) {
        console.error("Auto-login error:", error);
      }
    }
    return false;
  },

  logout: async () => {
    const user = get().user;
    try {
      if (user?.master_url && user?.sopnoid) {
        const { time, currentDate } = buildTimestamps();
        const formData = new FormData();
        formData.append("poster", user.sopnoid);
        formData.append("startTime", "0");
        formData.append("endTime", time);
        formData.append("cdate", currentDate);
        formData.append("state", "0");
        formData.append("sessionId", get().uniqId);
        formData.append("ip", get().ip);
        await axios.post(
          `${user.master_url}/profile/login/api/utask_session_new.php`,
          formData
        );
      }
    } catch (error) {
      console.error("Logout Error:", error);
    } finally {
      get().clearSession();
    }
  },

  // ---------- socket ----------
  connectSocket: () => {
    const { user, socket } = get();
    // Guard on the socket itself (not `.connected`) so two quick calls
    // can't open two connections while the first is still handshaking.
    if (!user?.sopnoid || socket || !isAccountVerified(user)) return;

    const newSocket = io(SOCKET_URL, { transports: ["websocket"] });
    newSocket.emit("addUser", user.sopnoid); // buffered until connected
    newSocket.io.on("reconnect", () => newSocket.emit("addUser", user.sopnoid));
    newSocket.on("updateUserList", (allUsers) => {
      set({ onlineUsers: allUsers.map((u) => u.userId) });
    });
    set({ socket: newSocket });
  },

  disconnectSocket: () => {
    const socket = get().socket;
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
    }
    set({ socket: null });
  },

  // ---------- data fetchers (all guard on a real user first) ----------
  fetchInvitationNotification: async () => {
    const user = get().user;
    if (!user?.sopnoid) return;
    const formData = new FormData();
    formData.append("SopnoID", user.sopnoid);
    formData.append("action", "fetch");
    formData.append("sta", "0");
    try {
      const response = await axios.post(
        `${user.master_url}/profile/login/api/room/noti_ro_req_n.php`,
        formData
      );
      set({ invitationNotification: response.data });
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  },

  fetchMessageNotification: async () => {
    const user = get().user;
    if (!user?.sopnoid) return;
    const formData = new FormData();
    formData.append("SopnoID", user.sopnoid);
    formData.append("action", "check");
    try {
      const response = await axios.post(
        `${user.master_url}/profile/message/message_notif.php`,
        formData
      );
      set({ messageNotification: response.data, allNotification: response.data });
    } catch (error) {
      console.error("Error fetching message notifications:", error);
    }
  },

  fetchNotes: async () => {
    const user = get().user;
    if (!user?.sopnoid) return;
    const date = new Date();
    const formData = new FormData();
    formData.append("SopnoID", user.sopnoid);
    formData.append("year", date.getFullYear());
    formData.append("month", date.getMonth() + 1);
    formData.append("action", "fetch");
    try {
      const response = await axios.post(
        `${user.master_url}/profile/login/calender/utask_note_selected.php`,
        formData
      );
      const filteredData = response.data?.filter((r) => r?.poster === user.sopnoid);
      set({ noteData: filteredData });
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  },

  fetchMsgUserList: async () => {
    const user = get().user;
    if (!user?.sopnoid) return;
    try {
      const response = await axios.post(
        `${user.master_url}/profile/login/api/api_tracker_chat.php?request_type=getMroomAll&item=all&SopnoID=${user.sopnoid}&pref=&cRefer=${user.c_refer}&roomid=&page=${get().userPage}`
      );
      if (!Array.isArray(response?.data)) return;
      const sortedData = [...response.data].sort((a, b) => {
        const aOnline = a?.isOnline === "1";
        const bOnline = b?.isOnline === "1";
        if (a?.poster_info?.[0]?.Name && b?.poster_info?.[0]?.Name) {
          if (aOnline && !bOnline) return -1;
          if (!aOnline && bOnline) return 1;
        }
        return 0;
      });
      set({ msgUserList: sortedData });
    } catch (error) {
      console.error("Error fetching message list:", error);
    }
  },
}));

// Persistence summary
// - `user` lives at localStorage["gh_sl@g"] as base64(ASCII-safe JSON) and, for
//   SSO, in the ".songjukti.com" cookie "songjukti_user". setSession() is the
//   only writer; clearSession() is the only remover.
// - `rememberedEmail` is a plain, non-sensitive string. Passwords are never stored.
// - ip / uniqId / sockets / notification lists are in-memory only.